import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// #87: Begründungs-Dialog für den Close-Guard (Router: Schließen ohne bestandenen Nachtest
// verlangt `comment`). Folgt bewusst demselben Muster wie SidePanel (natives <dialog>, eigenes
// Escape-Handling per keydown, Fokus rein beim Öffnen) statt eines nackten fixed-div — genau das
// war der bekannte A11y-Befund am bestehenden DefectCreateDialog (kein role, kein Escape, kein
// Fokus-Handling). Anders als SidePanel aber zentriert statt als rechte Schublade: eine
// Ja/Nein-Rückfrage mit einem Textfeld braucht keinen Vollhöhen-Slide-over, und würde — von
// DefectDetail aus geöffnet, das selbst schon in einem SidePanel steckt — sonst zwei
// übereinanderliegende Schubladen samt doppeltem Backdrop ergeben.
export function RetestGuardDialog({
  pending,
  reason = 'retest_missing',
  reporterName,
  onCancel,
  onConfirm,
}: {
  pending: boolean;
  // #194: derselbe Dialog für zwei Server-Guards — Nachtest (#87) und Melder-Bestätigung. Nur
  // Überschrift und Hinweistext unterscheiden sich; Begründungsfeld und Knopf bleiben gleich.
  reason?: 'retest_missing' | 'reporter_pending';
  // Name des wartenden Melders (`awaiting_reporter_name`); fehlt er, steht ein neutraler Text.
  reporterName?: string | null | undefined;
  onCancel: () => void;
  onConfirm: (comment: string) => void;
}) {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    // Review-Fund (Important 1): SidePanel haengt ebenfalls einen document-keydown-Listener fuer
    // Escape — in der Bubble-Phase (Standard), s. side-panel.tsx. Oeffnet dieser Dialog aus einer
    // Detailansicht heraus, die selbst schon im Board-SidePanel steckt, wuerden OHNE Gegenmassnahme
    // BEIDE Listener auf dasselbe Escape reagieren: Guard-Dialog UND SidePanel schlossen in einem
    // Tastendruck, obwohl nur der Dialog gemeint war. Fix: dieser Listener haengt in der
    // CAPTURE-Phase (dritter Parameter true) — Capture-Listener auf `document` feuern IMMER vor
    // Bubble-Listenern auf demselben Knoten, unabhaengig von der Registrierreihenfolge, weil das
    // Ereignis document waehrend der Capture-Phase durchlaeuft, bevor es das eigentliche Ziel
    // erreicht und ueberhaupt in die Bubble-Phase eintritt. stopPropagation() hier verhindert, dass
    // das Ereignis ueberhaupt bis zum Ziel (und damit anschliessend zurueck zur Bubble-Phase an
    // SidePanels Listener) durchdringt — Escape wird vollstaendig "verbraucht", solange dieser
    // Dialog offen ist.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCancel();
    };
    document.addEventListener('keydown', onKey, true);
    dialogRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const trimmed = comment.trim();
  const title =
    reason === 'reporter_pending'
      ? t('defect.guard.reporterPending.title')
      : t('defects.retestGuard.title');
  const body =
    reason === 'reporter_pending'
      ? reporterName
        ? t('defect.guard.reporterPending.body', { name: reporterName })
        : t('defect.guard.reporterPending.bodyNoName')
      : t('defects.retestGuard.body');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={onCancel}
        className="absolute inset-0"
      />
      <dialog
        ref={dialogRef}
        open
        tabIndex={-1}
        aria-modal="true"
        aria-label={title}
        className="relative z-10 m-0 w-full max-w-md space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4 text-text-body shadow-xl outline-none"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label={t('common.close')}
            className="rounded-btn px-2 py-1 text-text-muted hover:text-text-primary"
          >
            ✕
          </button>
        </div>
        <p className="text-sm text-text-muted">{body}</p>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {t('defects.retestGuard.commentLabel')}
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="min-h-[72px] rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
          />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            disabled={pending || trimmed.length === 0}
            onClick={() => onConfirm(trimmed)}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t('defects.retestGuard.confirm')}
          </button>
        </div>
      </dialog>
    </div>
  );
}
